import aboutUsPhoto from '@presentation/assets/images/aboutus-photo.png'
import SeoHead from '@presentation/seo/SeoHead'

const ABOUT_SECTIONS = [
  {
    title: 'The Story of PD Buddy and No Silver Bullet 4PD: A Shared Defiance',
    paragraphs: [
      'When Michel was diagnosed with Parkinson\'s six years ago, the medical advice he received was less of a treatment plan and more of a countdown. His neurologist delivered a prognosis with the clinical warmth of a weather report: Michel had a "good five years" before his mobility would vanish. He was handed a prescription, told to prepare for higher doses, and essentially shown the exit.',
      'It was a brutal, life-altering moment, but it sparked a profound sense of defiance. Beatrice and Michel refused to accept that a timer had been set on his life. They had their doubts about that five-year deadline, so they traded despair for data. They embarked on a global quest, consulting with top-tier neuroscientists, nutritionists, and researchers to find out what happens when you stop being a passive patient and start being an active architect of your own health.',
      'What they found changed everything: the picture is not nearly as bleak as that first consultation suggested. There is a "silent majority" of people with the condition who have remained physically and mentally fit for decades. These individuals are not lucky; they are disciplined. They have stayed ahead of the curve through a strategic cocktail of exercise, nutrition, mindset, and social connection.',
    ],
  },
  {
    title: 'Turning a Diagnosis into a Lifestyle Choice',
    paragraphs: [
      'The duo realised that while there is "no silver bullet" for the condition, there is a way to fight back. They decided to turn their lives into a masterclass in living well. They swapped the hectic, polluted streets of London for the green hills of Oxfordshire and treated Michel\'s routine like a professional athlete\'s training camp.',
      'Today, Michel\'s daily life is a testament to the power of routine. He tackles two hours of physical exercise six days a week, from weights to cross-training. Every morning involves 30 minutes of brain training alongside his breakfast. Once a sceptic of "woo-woo" practices, he is now a daily meditator, using mindfulness to keep anxiety at bay. He even sticks to a vocal exercise routine he finds incredibly boring - because it works.',
      'Nutrition was the next frontier. The family pivoted to a Mediterranean-style diet, eliminating the "brain poisons" like alcohol and ultra-processed foods. They mastered the art of protein-timing, ensuring Michel\'s medication is never blocked by his meals. It sounds like a lot, but as they discovered, once the whole family joins the journey, the "work" simply becomes the way they live.',
    ],
  },
  {
    title: 'The Birth of PD Buddy and No Silver Bullet 4PD',
    paragraphs: [
      'As the routine took hold, Beatrice - a technologist with a "can-fix-it" spirit - built the tools she wished they had from day one. She created PD Buddy to help Michel track his symptoms and habits. It did not stay a family secret for long; within a year, over 3,000 people had joined. Today, it is a thriving community of thousands following the "PD Buddy way."',
      'While Beatrice built the digital infrastructure, Michel launched No Silver Bullet 4PD. He began interviewing the world\'s leading experts to bridge the gap between high-level science and the person sitting at home wondering what to eat for lunch. Their webinars have become a global phenomenon, reaching half a million views annually and proving that knowledge really is the best medicine.',
    ],
  },
  {
    title: 'The Vision: A Wake-Up Call, Not a Death Sentence',
    paragraphs: [
      'The collaboration between PD Buddy and No Silver Bullet 4PD is built on the simple, punchy conviction that Parkinson\'s is not a death sentence but a wake-up call, driving a mission to empower people to look beyond the pill bottle and become well-informed generalists in their own care. By providing a direct line to world-class experts, they transform what can often be a lonely journey into a powerful collective mission, ultimately motivating individuals to move from the passenger seat to the steering wheel of their own health.',
      'Six years after his "five-year" expiration date, Michel is not immobile. He is in control. Some symptoms have shifted, but his dedication remains unshakable. The duo has proven that while you cannot choose the diagnosis, you can absolutely choose the lifestyle. They did not just join Michel\'s journey; they boosted the entire family\'s well-being. They refused to surrender, and now they are helping thousands of others do the same.',
    ],
  },
]

export default function AboutPage() {
  return (
    <main className="bg-background py-8 sm:py-12">
      <SeoHead
        title="About Parkinson's Insights"
        description="Learn the story behind Parkinson's Insights, PD Buddy, and No Silver Bullet 4PD, and how the team supports people living with Parkinson's."
        canonicalPath="/about"
      />
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <div className="rounded-2xl border border-border-light bg-surface p-6 shadow-sm sm:p-8">
          <header className="border-b border-border-light pb-5 sm:pb-6">
            <h1 className="text-3xl font-bold tracking-tight text-primary sm:text-4xl">
              About Parkinson&apos;s Insights
            </h1>
          </header>
          {ABOUT_SECTIONS.map((section, index) => (
            <section
              key={section.title}
              className="py-5 first:pt-0 last:pb-0 sm:py-6"
            >
              <h2 className="text-2xl font-bold tracking-tight text-primary sm:text-3xl">
                {section.title}
              </h2>

              <div className="mt-4 space-y-4">
                {index === 0 && (
                  <figure className="mx-auto mb-4 w-full max-w-xs overflow-hidden rounded-xl border border-border-light bg-background sm:float-left sm:mb-4 sm:mr-6 sm:ml-0 sm:w-1/3 sm:max-w-none">
                    <img
                      src={aboutUsPhoto}
                      alt="Beatrice and Michel"
                      className="h-auto w-full object-cover"
                    />
                  </figure>
                )}

                {section.paragraphs.map((paragraph) => (
                  <p
                    key={paragraph}
                    className="text-base leading-relaxed text-text-primary"
                  >
                    {paragraph}
                  </p>
                ))}

                {index === 0 && <div className="clear-both" />}
              </div>

              {section.title ===
                'The Vision: A Wake-Up Call, Not a Death Sentence' && (
                <blockquote className="mt-6 rounded-xl border-l-4 border-primary bg-background p-4 sm:p-5">
                  <p className="text-base font-semibold leading-relaxed text-primary">
                    Parkinson&apos;s is not a death sentence - it can be a wake-up
                    call to reclaim agency, build routines that work, and stay in the
                    steering wheel of your own health.
                  </p>
                </blockquote>
              )}
            </section>
          ))}
        </div>
      </div>
    </main>
  )
}
